import { supabase } from '../supabase';
import { SchemaService } from './schemaService';

/**
 * Serviço para funções administrativas do sistema de recomendação
 */
export class AdminService {
  private schemaService: SchemaService;
  
  constructor() {
    this.schemaService = new SchemaService();
  }
  
  /**
   * Cria a função RPC para recomendações
   */
  public async createRecommendationRPC(): Promise<boolean> {
    try {
      // Criar função para gerenciar tabela de recomendações
      const { error } = await supabase.rpc('create_recommendation_function');
      
      if (error) {
        console.error('Erro ao criar função RPC de recomendações:', error);
        return false;
      }
      
      console.log('Função RPC de recomendações criada com sucesso');
      return true;
    } catch (error) {
      console.error('Exceção ao criar função RPC:', error);
      return false;
    }
  }
  
  /**
   * Cria a função RPC para alteração da tabela
   */
  public async createAdminAlterTableRPC(): Promise<boolean> {
    try {
      // Criar função para alterar tabela de recomendações (adicionar coluna de algoritmo)
      const { error } = await supabase.rpc('create_admin_alter_table_function');
      
      if (error) {
        console.error('Erro ao criar função RPC para alteração de tabela:', error);
        return false;
      }
      
      console.log('Função RPC para alteração de tabela criada com sucesso');
      return true;
    } catch (error) {
      console.error('Exceção ao criar função RPC de alteração:', error);
      return false;
    }
  }
  
  /**
   * Executa SQL administrativo
   */
  public async executeAdminSQL(sql: string): Promise<boolean> {
    try {
      const { error } = await supabase.rpc('execute_admin_sql', { sql_command: sql });
      
      if (error) {
        console.error('Erro ao executar SQL administrativo:', error);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Exceção ao executar SQL administrativo:', error);
      return false;
    }
  }
  
  /**
   * Verifica e corrige o schema de recomendações
   */
  public async verifyAndFixRecommendationsSchema(): Promise<boolean> {
    try {
      // Verificar se a tabela existe
      const { data, error } = await supabase
        .from('recommendations')
        .select('id')
        .limit(1);
        
      if (error) {
        console.log('Tabela de recomendações não existe, criando...');
        return await this.createRecommendationRPC();
      }
      
      // Verificar se coluna algorithm existe
      const { data: columns, error: columnsError } = await supabase
        .rpc('get_table_columns', { p_table_name: 'recommendations' });
        
      if (columnsError) {
        console.error('Erro ao verificar colunas da tabela:', columnsError);
        return false;
      }
      
      // Buscar coluna 'algorithm'
      const hasAlgorithmColumn = columns.some((col: any) => col.column_name === 'algorithm');
      
      if (!hasAlgorithmColumn) {
        console.log('Coluna algorithm não existe, adicionando...');
        return await this.createAdminAlterTableRPC();
      }
      
      console.log('Schema da tabela de recomendações verificado e correto');
      return true;
    } catch (error) {
      console.error('Exceção ao verificar schema:', error);
      return false;
    }
  }
  
  /**
   * DEPRECATED: Verifica e cria a tabela user_similarity.
   * Use a tabela user_similarities.
   */
  public async createUserSimilarityTable(): Promise<boolean> {
    console.warn("DEPRECATED: A função createUserSimilarityTable foi chamada, mas a tabela 'user_similarity' não é mais usada. Use 'user_similarities'.");
    // Retorna true para não quebrar chamadas existentes, mas não faz nada.
    return true;
    /* REMOVED LOGIC
    try {
      // Verificar se a tabela já existe
      const { data, error } = await supabase
        .from('user_similarity') // Referenced old table
        .select('id')
        .limit(1);

      // Se não houver erro, a tabela já existe
      if (!error) {
        console.log('Tabela user_similarity já existe');
        return true;
      }

      // Criar a tabela
      const createTableSQL = `
        CREATE TABLE IF NOT EXISTS user_similarity (
          id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
          user_id UUID REFERENCES auth.users(id),
          target_user_id UUID REFERENCES auth.users(id),
          similarity_score FLOAT NOT NULL,
          created_at TIMESTAMP WITH TIME ZONE DEFAULT now()
        );

        CREATE INDEX IF NOT EXISTS idx_user_similarity_user_id ON user_similarity(user_id);
        CREATE UNIQUE INDEX IF NOT EXISTS idx_user_similarity_pair ON user_similarity(user_id, target_user_id);
      `;

      const result = await this.executeAdminSQL(createTableSQL);

      if (result) {
        console.log('Tabela user_similarity criada com sucesso');
        return true;
      } else {
        console.error('Falha ao criar tabela user_similarity');
        return false;
      }
    } catch (error) {
      console.error('Exceção ao criar tabela de similaridade:', error);
      return false;
    }
    */
  }
  
  /**
   * Verifica e atualiza todo o esquema do banco de dados
   */
  public async verifyAndUpdateDatabaseSchema(): Promise<boolean> {
    try {
      // Verificar e corrigir tabela de recomendações
      const recommendationsOk = await this.verifyAndFixRecommendationsSchema();
      
      // Remover verificação da tabela de similaridade deprecada
      // const similarityOk = await this.createUserSimilarityTable(); 
      
      return recommendationsOk; // Retornar apenas o status da verificação de recomendações
    } catch (error) {
      console.error('Erro ao verificar e atualizar esquema do banco:', error);
      return false;
    }
  }
}

// Criar instância para exportação simples
const adminService = new AdminService();

// Exportar funções para uso direto
export const createRecommendationRPC = adminService.createRecommendationRPC.bind(adminService);
export const createAdminAlterTableRPC = adminService.createAdminAlterTableRPC.bind(adminService);
export const executeAdminSQL = adminService.executeAdminSQL.bind(adminService);
export const verifyAndFixRecommendationsSchema = adminService.verifyAndFixRecommendationsSchema.bind(adminService);
export const createUserSimilarityTable = adminService.createUserSimilarityTable.bind(adminService);
export const verifyAndUpdateDatabaseSchema = adminService.verifyAndUpdateDatabaseSchema.bind(adminService); 